import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { apiFetch } from './api';
import { useAuth } from './auth';

/**
 * Tells the API which page is open, for the admin's visitor log.
 *
 * nginx sees only the first load of the app; every page after it is a
 * client-side navigation. This fills that in, signed in when the user is —
 * which is why it waits for the session to be restored — and on the first
 * report adds what only the browser knows: time zone, screen, languages.
 * Fire-and-forget: a failed report is not the user's problem.
 */
export function VisitBeacon() {
  const { pathname } = useLocation();
  const { status } = useAuth();
  const sentClient = useRef(false);
  const lastPath = useRef<string | null>(null);

  useEffect(() => {
    if (status === 'loading' || lastPath.current === pathname) return;
    lastPath.current = pathname;
    const client = sentClient.current ? undefined : describeClient();
    sentClient.current = true;
    void apiFetch('/visits/view', {
      method: 'POST',
      raw: true,
      body: { path: pathname, ...(client ? { client } : {}) },
    }).catch(() => undefined);
  }, [pathname, status]);

  return null;
}

function describeClient() {
  try {
    return {
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      languages: [...(navigator.languages ?? [navigator.language])].slice(0, 10),
      screen: `${window.screen.width}×${window.screen.height}`,
      viewport: `${window.innerWidth}×${window.innerHeight}`,
      pixelRatio: Math.round(window.devicePixelRatio * 100) / 100,
      platform:
        (navigator as Navigator & { userAgentData?: { platform?: string } }).userAgentData
          ?.platform ?? navigator.platform,
      touch: navigator.maxTouchPoints > 0,
      standalone: window.matchMedia('(display-mode: standalone)').matches,
      ...(document.referrer ? { referrer: document.referrer.slice(0, 500) } : {}),
    };
  } catch {
    return undefined;
  }
}
